import React, { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'styled-components';

import { zIndexes } from '../../../utils/zIndexes';

export const MaxSizeMediaContentContext = React.createContext<{
  height?: number;
  width?: number;
}>({
  height: 9,
  width: 16,
});

const MaxSizeMediaContent = ({
  children,
  aspectRatio = 16 / 9, // w/h=2
  style,
}: {
  children: React.ReactNode;
  aspectRatio?: number;
  style?: React.CSSProperties;
}) => {
  const mediaContentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [maxWidth, setMaxWidth] = useState(0);
  const observer = useRef(
    new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }

        // Only care about the first element, we expect one element ot be watched
        const { height: contentHeight } = entries[0].contentRect;
        const width = mediaContentRef.current?.offsetWidth || 16;
        const height = mediaContentRef.current?.offsetHeight || 9;

        if (width && height) {
          setMaxWidth(width);
          if (width / aspectRatio > contentHeight) {
            // the aspect ration calculation is larger than parent
            setMaxHeight(contentHeight);
          } else {
            setMaxHeight(width / aspectRatio);
          }
        }
      });
    }),
  );

  useEffect(() => {
    const observerRef = observer.current;
    const mediaRef = mediaContentRef.current;
    if (mediaRef) {
      observer.current.observe(mediaRef);
    }

    return () => {
      if (mediaRef && observerRef) {
        observerRef.unobserve(mediaRef);
      }
    };
  }, [mediaContentRef, observer]);
  return (
    <MediaPreviewContainer style={style ? style : {}} ref={mediaContentRef}>
      <MediaPreviewContentWrapper $aspectRatio={aspectRatio} $maxHeight={maxHeight}>
        <MaxSizeMediaContentContext.Provider
          value={{
            height: maxHeight,
            width: maxWidth,
          }}
        >
          {children}
        </MaxSizeMediaContentContext.Provider>
      </MediaPreviewContentWrapper>
    </MediaPreviewContainer>
  );
};

export default MaxSizeMediaContent;

const MediaPreviewContainer = styled.div`
  height: 100%;
  max-height: 100%;
  width: 100%;
  position: relative;
  display: grid;
  place-items: center;
  z-index: ${zIndexes('MAX_SIZE_MEDIA_CONTENT')};
`;

const MediaPreviewContentWrapper = styled.div<{
  $aspectRatio: number;
  $maxHeight?: number;
}>`
  max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  position: relative;
  z-index: ${zIndexes('MAX_SIZE_MEDIA_CONTENT')};
`;
